import * as _ from 'lodash';
import ReactTable from 'react-table';
import type { FunctionComponent } from 'react';
import type { ComponentPropsGetterR, SortingRule } from 'react-table';

import { MINIMUM_PAGE_SIZE } from './index';
import useStyledColumns from './useStyledColumns';
import type { SimpleReactTableProps } from './index';

import styles from './SimpleReactTable.module.less';

const getTdProps = _.constant({
  className: styles.tableData,
});

const noopGetter = _.constant({});

/**
 * SimpleReactTableWithPagination is a version of SimpleReactTable that allows
 * for controlled pagination from outside the component, useful when working
 * with paginated API responses.
 */
const SimpleReactTableWithPagination: FunctionComponent<
  SimpleReactTableProps & {
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number, page: number) => void;
    totalPages?: number;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
  }
> = ({
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalPages,
  defaultPageSize,
  pageSizeOptions,
  ...props
}) => {
  const styledColumns = useStyledColumns(props.columns);
  const onSortedChanged = props.onSortedChanged || (() => {});
  return (
    <ReactTable
      className="simple-react-table"
      data={props.data}
      columns={styledColumns}
      minRows={props.minRows ?? 5}
      collapseOnDataChange={false}
      collapseOnSortingChange={false}
      onSortedChange={newSortMode => onSortedChanged(newSortMode[0] as SortingRule)}
      defaultSorted={props.defaultSortMode}
      // Pagination props
      showPagination
      page={page - 1}
      pageSize={pageSize}
      onPageChange={newPage => onPageChange(newPage + 1)}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={pageSizeOptions || [MINIMUM_PAGE_SIZE, 50, 100]}
      defaultPageSize={defaultPageSize}
      pages={totalPages}
      manual // Tell react-table that we're handling pagination externally
      // Other props
      getTdProps={getTdProps}
      getTrProps={(props.getTrProps || noopGetter) as ComponentPropsGetterR}
      getTrGroupProps={(props.getTrGroupProps || noopGetter) as ComponentPropsGetterR}
      sortable={props.isSortable ?? true}
      loading={props.isLoading}
    />
  );
};

export default SimpleReactTableWithPagination;
